import { Component, ErrorHandler, Input, Injector, OnDestroy, OnInit, ChangeDetectionStrategy, inject, signal } from '@angular/core'
import { applyEach, disabled, FieldTree, form, ReadonlyFieldTree, required, validateTree } from '@angular/forms/signals'
import { MatSnackBar } from '@angular/material/snack-bar'

import { BehaviorSubject, Subject, of } from 'rxjs'
import { map, switchMap, takeUntil, tap } from 'rxjs/operators'

import { Ajoneuvo, KirjanpidonUlkopuolisetKulut } from '@lemon/core-firebase/model/kayttaja'
import { KopioijaPalvelu } from '@lemon/core-firebase/service/kopioija.service'
import { DateService } from '@lemon/core/service/date.service'
import { FirebaseLemonaid, FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'
import { arvonMuutokset, asetaArvo } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { TimestampService } from 'app/_jaettu-angular/service/timestamp-service'
import { LuoUlkopuolisetKulutTaikalinkkiRequest, LuoUlkopuolisetKulutTaikalinkkiResponse } from 'app/_jaettu-lemonator/model/kirjanpito'
import { FormValidationService } from '../../_jaettu-angular/service/form-validation.service'
import { LadataanService } from '../../_jaettu-angular/service/ladataan.service'

interface AjoneuvoLomake {
  rekisteritunnus: string
  tyyppi: Ajoneuvo['tyyppi'] | null
  kokonaisKmMaara: number | null
  tyoAjoissaKm: number | null
}

interface UlkopuolisetKulutLomake {
  eiIlmoitettavaa: boolean | null
  ajoneuvot: AjoneuvoLomake[]
  tiedotPerustuvat: KirjanpidonUlkopuolisetKulut['tiedotPerustuvat'] | null
  yli10hKotimaanMatkojenLukumaara: number | null
  yli6hKotimaanMatkojenLukumaara: number | null
  ulkomaanMatkapaivienLukumaara: number | null
  ulkomaanPaivarahojenYhteismaara: number | null
  kotiYrityksenTyotilana: KirjanpidonUlkopuolisetKulut['kotiYrityksenTyotilana'] | null
}

@Component({
  selector: '[app-ulkopuoliset-kulut]',
  templateUrl: './ulkopuoliset-kulut-lemonator.component.html',
  styleUrls: ['./ulkopuoliset-kulut-lemonator.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class UlkopuolisetKulutLemonatorComponent implements OnInit, OnDestroy {
  private yearSubject: BehaviorSubject<number> = new BehaviorSubject(null)

  @Input() set setYear(value: number) {
    this.yearSubject.next(value)
    this.commonError.set(null)
  }
  @Input() asiakasAvain: string

  private ngUnsubscribe: Subject<void> = new Subject<void>()
  private readonly _injector = inject(Injector)
  private readonly _tiedotPerustuvatPoissa = signal(true)
  private readonly _tyoajotLiianSuuret = signal<boolean[]>([])
  private readonly _ajoneuvotMuuttuivat = new Subject<AjoneuvoLomake[]>()
  private _ajoneuvorivienLopetukset: Subject<void>[] = []

  lomake: FieldTree<UlkopuolisetKulutLomake>
  readonly commonError = signal<string>(undefined)
  readonly lomakePaivitetty = signal<string>(undefined)
  lomakkeenPaivittaja: string

  private _ulkopuolisetKulut: KirjanpidonUlkopuolisetKulut
  private _ajoneuvot: Ajoneuvo[] = []

  readonly onIlmoitettavaa = signal(true)

  private _saveStarted: boolean

  paasaantoinenKotiVahennys: string = '940' // 2023
  osaaikainenKotiVahennys: string = '470' // 2023
  satunnainenKotiVahennys: string = '235' // 2023

  constructor(
    private _errorHandler: ErrorHandler,
    private _ladataanService: LadataanService,
    private _validationService: FormValidationService,
    private _snackbar: MatSnackBar,
    private _firebaseLemonaid: FirebaseLemonaid,
    private _copyService: KopioijaPalvelu,
    private _timestampService: TimestampService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _firebase: FirebaseLemonator,
    private _dateService: DateService,
  ) {}

  ngOnInit() {
    this.lomake = form(
      signal<UlkopuolisetKulutLomake>({
        eiIlmoitettavaa: null,
        ajoneuvot: [],
        tiedotPerustuvat: null,
        yli10hKotimaanMatkojenLukumaara: null,
        yli6hKotimaanMatkojenLukumaara: null,
        ulkomaanMatkapaivienLukumaara: null,
        ulkomaanPaivarahojenYhteismaara: null,
        kotiYrityksenTyotilana: null,
      }),
      polku => {
        applyEach(polku.ajoneuvot, rivi => {
          required(rivi.rekisteritunnus)
          required(rivi.tyyppi)
          required(rivi.kokonaisKmMaara)
          required(rivi.tyoAjoissaKm)
          validateTree(rivi, ({ index, fieldTree }) => (this._tyoajotLiianSuuret()[index()] ? { kind: 'toolarge', fieldTree: fieldTree.tyoAjoissaKm as ReadonlyFieldTree<unknown> } : null))
        })
        disabled(polku.tiedotPerustuvat, () => this._tiedotPerustuvatPoissa())
        required(polku.tiedotPerustuvat)
      },
      { injector: this._injector },
    )
    const muutokset = <T>(kentta: FieldTree<T>) => arvonMuutokset(kentta, { injector: this._injector })

    if (this.asiakasAvain) {
      muutokset(this.lomake.ajoneuvot)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(ajoneuvot => this._ajoneuvotMuuttuivat.next(ajoneuvot))

      this.yearSubject
        .asObservable()
        .pipe(
          tap(() => this._ladataanService.aloitaLataaminen()),
          switchMap(vuosi => {
            if (!vuosi) {
              return of(null)
            }
            return this._firebaseLemonaid
              .firestoreDoc<KirjanpidonUlkopuolisetKulut>('customers/' + this.asiakasAvain + '/customer-ulkopuoliset-kulut/' + this.asiakasAvain + '_' + vuosi)
              .listen()
              .pipe(
                map(firestoresta => {
                  this.lomakePaivitetty.set(firestoresta?.paivitetty ? this._dateService.muotoilePaivaJaAikaDate(firestoresta?.paivitetty?.toDate(), 'fi') : 'ei koskaan')
                  // this.lomakkeenPaivittaja = firestoresta?.paivittaja || ''

                  if (firestoresta) {
                    return firestoresta
                  }
                  const blankForm: KirjanpidonUlkopuolisetKulut = this.createBlankForm(this.asiakasAvain)
                  return blankForm
                }),
              )
          }),
          takeUntil(this.ngUnsubscribe),
        )
        .subscribe(lomake => {
          this._ajoneuvotMuuttuivat.pipe(takeUntil(this.ngUnsubscribe)).subscribe(value => {
            if (!value?.length) {
              this._asetaTiedotPerustuvatPoissa(true)
            } else {
              this._asetaTiedotPerustuvatPoissa(false)
            }
          })

          if (lomake) {
            this._ulkopuolisetKulut = this._copyService.cloneObjectDeep(lomake)
            this.alustaLomakkeenTiedot(lomake)
          } else {
            this.commonError.set('Tapahtui tuntematon virhe. Ole hyvä ja yritä uudelleen.')
          }

          muutokset(this.lomake.tiedotPerustuvat)
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(value => {
              this._ulkopuolisetKulut.tiedotPerustuvat = this._validationService.processValue(value)
            })

          muutokset(this.lomake.yli10hKotimaanMatkojenLukumaara)
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(value => {
              this._ulkopuolisetKulut.yli10hKotimaanMatkojenLukumaara = this._validationService.processValue(value)
            })
          muutokset(this.lomake.yli6hKotimaanMatkojenLukumaara)
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(value => {
              this._ulkopuolisetKulut.yli6hKotimaanMatkojenLukumaara = this._validationService.processValue(value)
            })
          muutokset(this.lomake.ulkomaanMatkapaivienLukumaara)
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(value => {
              this._ulkopuolisetKulut.ulkomaanMatkapaivienLukumaara = this._validationService.processValue(value)
            })
          muutokset(this.lomake.ulkomaanPaivarahojenYhteismaara)
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(value => {
              this._ulkopuolisetKulut.ulkomaanPaivarahojenYhteismaara = this._validationService.processValue(value)
            })
          muutokset(this.lomake.kotiYrityksenTyotilana)
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(value => {
              this._ulkopuolisetKulut.kotiYrityksenTyotilana = this._validationService.processValue(value)
            })

          this._ladataanService.lopetaLataaminen()
        })

      muutokset(this.lomake.eiIlmoitettavaa)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(eiIlmoitettavaa => {
          this.onIlmoitettavaa.set(!eiIlmoitettavaa)
          this._ulkopuolisetKulut.eiIlmoitettavaa = eiIlmoitettavaa

          if (eiIlmoitettavaa) {
            this._asetaTiedotPerustuvatPoissa(true)
          } else {
            const onkoAjoneuvoja = !!this.lomake.ajoneuvot().value()?.length

            if (onkoAjoneuvoja) {
              this._asetaTiedotPerustuvatPoissa(false)
            }
          }
        })
    }
  }

  private _asetaTiedotPerustuvatPoissa(poissa: boolean) {
    this._tiedotPerustuvatPoissa.set(poissa)
    // Käytöstilan vaihto lähettää valinnan arvon tilaajille
    asetaArvo(this.lomake.tiedotPerustuvat, this.lomake.tiedotPerustuvat().value())
  }

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string) {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }

  poistaAjoneuvo(index: number) {
    this._ajoneuvorivienLopetukset[index].next()
    this._ajoneuvorivienLopetukset[index].complete()
    this._ajoneuvorivienLopetukset.splice(index, 1)
    asetaArvo(
      this.lomake.ajoneuvot,
      this.lomake
        .ajoneuvot()
        .value()
        .filter((_, i) => i !== index),
      { ilmoita: false },
    )
    this._tyoajotLiianSuuret.update(liput => liput.filter((_, i) => i !== index))
    this._ajoneuvotMuuttuivat.next(this.lomake.ajoneuvot().value())
    this._ajoneuvot.splice(index, 1)
  }

  private _tyhjennaAjoneuvot() {
    if (!this.lomake.ajoneuvot().value().length) {
      return
    }
    for (const lopetus of this._ajoneuvorivienLopetukset) {
      lopetus.next()
      lopetus.complete()
    }
    this._ajoneuvorivienLopetukset = []
    asetaArvo(this.lomake.ajoneuvot, [], { ilmoita: false })
    this._tyoajotLiianSuuret.set([])
    this._ajoneuvotMuuttuivat.next([])
  }

  lisaaAjoneuvo(ajn?: Ajoneuvo) {
    const ajoneuvo: Ajoneuvo = {
      rekisteritunnus: ajn?.rekisteritunnus || '',
      tyyppi: ajn?.tyyppi || null,
      kokonaisKmMaara: ajn?.kokonaisKmMaara || null,
      tyoAjoissaKm: ajn?.tyoAjoissaKm || null,
    }

    const ajoneuvot = this.lomake.ajoneuvot().value()
    asetaArvo(
      this.lomake.ajoneuvot,
      [...ajoneuvot, { rekisteritunnus: ajoneuvo.rekisteritunnus, tyyppi: ajoneuvo.tyyppi, kokonaisKmMaara: ajoneuvo.kokonaisKmMaara, tyoAjoissaKm: ajoneuvo.tyoAjoissaKm }],
      { ilmoita: false },
    )
    this._tyoajotLiianSuuret.update(liput => [...liput, false])
    const rivi = this.lomake.ajoneuvot[ajoneuvot.length]
    const lopetus = new Subject<void>()
    const muutokset = <T>(kentta: FieldTree<T>) => arvonMuutokset(kentta, { injector: this._injector }).pipe(takeUntil(lopetus), takeUntil(this.ngUnsubscribe))
    const tarkistaTyoajot = () => {
      const indeksi = this._ajoneuvot.indexOf(ajoneuvo)
      const liianSuuret = ajoneuvo.kokonaisKmMaara < ajoneuvo.tyoAjoissaKm
      this._tyoajotLiianSuuret.update(liput => liput.map((lippu, i) => (i === indeksi ? liianSuuret : lippu)))
    }

    muutokset(rivi.rekisteritunnus).subscribe(value => {
      ajoneuvo.rekisteritunnus = value || null
    })
    muutokset(rivi.tyyppi).subscribe(value => {
      ajoneuvo.tyyppi = value || null
    })
    muutokset(rivi.kokonaisKmMaara).subscribe(value => {
      ajoneuvo.kokonaisKmMaara = value || null
      tarkistaTyoajot()
    })
    muutokset(rivi.tyoAjoissaKm).subscribe(value => {
      ajoneuvo.tyoAjoissaKm = value || null
      tarkistaTyoajot()
    })

    this._ajoneuvorivienLopetukset.push(lopetus)
    this._ajoneuvotMuuttuivat.next(this.lomake.ajoneuvot().value())
    this._ajoneuvot.push(ajoneuvo)
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next()
    this.ngUnsubscribe.complete()
  }

  async tallenna() {
    if (this._saveStarted) {
      return
    }
    if (!this.lomake().valid()) {
      this._validationService.merkitseKokoLomakeKosketuksi(this.lomake)
      return
    }

    // Show loading indicator
    this._saveStarted = true
    this._ladataanService.aloitaLataaminen()

    try {
      const batch = this._firebaseLemonaid.firestoreBatch()

      const now = this._timestampService.now()
      this._ulkopuolisetKulut.luotu = this._ulkopuolisetKulut.luotu || now
      this._ulkopuolisetKulut.paivitetty = now

      const kirjanpitajaTiedot = await this._kirjautunutKayttajaService.getKirjanpitajanTiedot()
      this._ulkopuolisetKulut.luoja = this._ulkopuolisetKulut.luoja || kirjanpitajaTiedot.uid
      this._ulkopuolisetKulut.paivittaja = kirjanpitajaTiedot.uid

      this._ulkopuolisetKulut.ajoneuvot = [...this._ajoneuvot]

      // Set disabled field as null
      if (this.lomake.tiedotPerustuvat().disabled()) {
        this._ulkopuolisetKulut.tiedotPerustuvat = null
      }
      // Save main doc
      batch.set('customers/' + this.asiakasAvain + '/customer-ulkopuoliset-kulut/' + this.asiakasAvain + '_' + this.yearSubject.value, this._ulkopuolisetKulut)

      // Create history entry
      const historyCopy = this._copyService.cloneObjectDeep(this._ulkopuolisetKulut)
      const historyAvain = this._firebaseLemonaid.firestoreCreateId()
      historyCopy.avain = historyAvain
      batch.set(
        'customers/' + this.asiakasAvain + '/customer-ulkopuoliset-kulut/' + this.asiakasAvain + '_' + this.yearSubject.value + '/customer-ulkopuoliset-kulut-history/' + historyAvain,
        historyCopy,
      )

      // Commit batch
      await batch.commit()

      // All is good, let's advance
      this._snackbar.open('Tallennus onnistui', 'OK', { duration: 5000, verticalPosition: 'top' })
      // Stop loading indicator
      this._ladataanService.lopetaLataaminen()
      this._saveStarted = false
    } catch (error) {
      this._saveStarted = false
      this._ladataanService.lopetaLataaminen()
      this.commonError.set('Tapahtui tuntematon virhe. Ole hyvä ja yritä uudelleen.')
      this._errorHandler.handleError(error)
    }
  }
  private createBlankForm(asiakasAvain: string): KirjanpidonUlkopuolisetKulut {
    const blank: KirjanpidonUlkopuolisetKulut = {
      avain: this._firebaseLemonaid.firestoreCreateId(),
      asiakasAvain: asiakasAvain,
      vuosi: this.yearSubject.value,
      ajoneuvot: [],
      tiedotPerustuvat: null,
      yli10hKotimaanMatkojenLukumaara: null,
      yli6hKotimaanMatkojenLukumaara: null,
      ulkomaanMatkapaivienLukumaara: null,
      ulkomaanPaivarahojenYhteismaara: null,
      kotiYrityksenTyotilana: null,
      paivitetty: null,
      paivittaja: null,
      luoja: null,
      luotu: null,
      poistettu: false,
    }
    return blank
  }
  private alustaLomakkeenTiedot(ulkopuolisetKulut: KirjanpidonUlkopuolisetKulut) {
    this._ajoneuvot = []
    this._tyhjennaAjoneuvot()

    // NB! Don't change order for these - timing issue with subscriptions
    if (ulkopuolisetKulut.ajoneuvot?.length) {
      for (const ajoneuvo of ulkopuolisetKulut.ajoneuvot) {
        this.lisaaAjoneuvo(ajoneuvo)
      }
    }

    asetaArvo(this.lomake.eiIlmoitettavaa, ulkopuolisetKulut.eiIlmoitettavaa || null)
    asetaArvo(this.lomake.tiedotPerustuvat, ulkopuolisetKulut.tiedotPerustuvat ?? null)
    asetaArvo(this.lomake.yli10hKotimaanMatkojenLukumaara, ulkopuolisetKulut.yli10hKotimaanMatkojenLukumaara ?? null)
    asetaArvo(this.lomake.yli6hKotimaanMatkojenLukumaara, ulkopuolisetKulut.yli6hKotimaanMatkojenLukumaara ?? null)
    asetaArvo(this.lomake.ulkomaanMatkapaivienLukumaara, ulkopuolisetKulut.ulkomaanMatkapaivienLukumaara ?? null)
    asetaArvo(this.lomake.ulkomaanPaivarahojenYhteismaara, ulkopuolisetKulut.ulkomaanPaivarahojenYhteismaara ?? null)
    asetaArvo(this.lomake.kotiYrityksenTyotilana, ulkopuolisetKulut.kotiYrityksenTyotilana ?? null)
  }

  async luoTaikalinkki() {
    const req: LuoUlkopuolisetKulutTaikalinkkiRequest = {
      asiakasAvain: this.asiakasAvain,
      verovuosi: this.yearSubject.value,
    }

    this._ladataanService.aloitaLataaminen()

    const taikalinkkiBlobType = 'text/plain'
    const getTaikalinkkiBlob = (taikalinkki: string): Blob => {
      return new Blob([taikalinkki], { type: taikalinkkiBlobType })
    }

    const taikalinkkiPromise: Promise<Blob> = this._firebase
      .functionsCall<LuoUlkopuolisetKulutTaikalinkkiRequest, LuoUlkopuolisetKulutTaikalinkkiResponse>('kirjanpidonUlkopuolisetKulutAnnaUusiTaikalinkki', req)
      .then(resp => {
        if (!resp || resp.e) {
          if (resp?.e === 'veroilmoitus-sent') {
            this._snackbar.open('Valitun vuoden veroilmoitus on jo lähetetty.', 'OK', { verticalPosition: 'top', duration: 1500 })
          } else {
            this._errorHandler.handleError(new Error(resp ? resp.e : 'unknown-error'))
          }
          return getTaikalinkkiBlob('')
        } else {
          this._snackbar.open('Taikalinkki on tallennettu leikepöydälle. Liitä (Cmd+V) se sähköpostin muokkauskentälle.', 'OK', { verticalPosition: 'top', duration: 1500 })
          return getTaikalinkkiBlob(resp.taikalinkki)
        }
      })
      .catch(err => {
        this._errorHandler.handleError(err)
        return getTaikalinkkiBlob('')
      })

    // The following clipboard write is supported on Chromium and Safari
    const clipboardItem = new ClipboardItem({ [taikalinkkiBlobType]: taikalinkkiPromise })
    await navigator.clipboard.write([clipboardItem])
    this._ladataanService.lopetaLataaminen()
  }
}
